/**
 * @file Contributor dashboard UI — pages/Dashboard/Contributor/Stages/A3/utils/a3ProcessFuelLayout.ts.
 */

import type { FormDataResponse } from "../../../../../../interfaces/formInterfaces";
import {
  insertIndexForNewFuelAtMaterial,
  shiftA2FuelValuesForInsert,
  shiftA2FuelValuesForRemove,
} from "../../A2/utils/a2FuelLayout";
import {
  materialIndexForVehicleFlatRow,
  totalVehicleRows,
} from "../../A2/utils/a2VehicleLayout";

/** Max fuel types per manufacturing sub-process (same cap as Form A2 per raw material). */
export const A3_MAX_FUELS_PER_PROCESS = 3;

const FUEL_CODE = "A3_PROCESS_FUEL_DETAILS";

/** Exported const — total A3Fuel Flat Rows. */
export const totalA3FuelFlatRows = totalVehicleRows;

/** Exported const — process Index For A3Fuel Flat Row. */
export const processIndexForA3FuelFlatRow = materialIndexForVehicleFlatRow;

/** Exported const — insert Index For New A3Fuel Slot. */
export const insertIndexForNewA3FuelSlot = insertIndexForNewFuelAtMaterial;

/** Exported function — process Fuel Flat Start. */
export function processFuelFlatStart(
  processIndex: number,
  counts: number[]
): number {
  let s = 0;
  for (let i = 0; i < processIndex; i++) {
    s += Math.max(0, counts[i] ?? 0);
  }
  return s;
}

/** Align `fuelsPerProcess` length to `processRowCount` (default 1 fuel slot per missing tail row). */
export function alignA3FuelsPerProcessRowCount(
  fuelsPerProcess: number[],
  processRowCount: number
): number[] {
  if (processRowCount <= 0) return [];
  if (fuelsPerProcess.length >= processRowCount) {
    return fuelsPerProcess.slice(0, processRowCount);
  }
  return [
    ...fuelsPerProcess,
    ...Array.from(
      { length: processRowCount - fuelsPerProcess.length },
      () => 1
    ),
  ];
}

/** Exported function — collect A3Process Fuel Field Names For Shift. */
export function collectA3ProcessFuelFieldNamesForShift(
  formData: FormDataResponse | null
): string[] {
  const sec = formData?.formSubSections?.find((s) => s.code === FUEL_CODE);
  return sec?.fields?.map((f) => f.name) ?? [];
}

/**
 * Bucket saved `A3_Fuel` rows by `processName` vs `A3_Process` rows, then flatten in process order
 * for CMS keys `*_A3_PROCESS_FUEL_DETAILS_rowN` (flat N).
 */
export function buildA3FuelLoadLayout(
  processRows: unknown[] | undefined,
  fuelRows: unknown[] | undefined
): { counts: number[]; flatItems: Record<string, unknown>[] } {
  const P = Math.max(1, processRows?.length ?? 1);
  const procNames = (processRows ?? []).map((p: any) =>
    String(p?.processName ?? "").trim().toLowerCase()
  );
  const buckets: Record<string, unknown>[][] = Array.from(
    { length: P },
    () => []
  );

  if (Array.isArray(fuelRows)) {
    for (const raw of fuelRows) {
      const item = (raw && typeof raw === "object" ? raw : {}) as Record<
        string,
        unknown
      >;
      const pname = String(
        item?.processName ?? item?.ProcessName ?? ""
      )
        .trim()
        .toLowerCase();
      let pi = 0;
      if (pname) {
        const idx = procNames.findIndex((pn) => pn && pn === pname);
        if (idx >= 0) pi = idx;
      }
      buckets[pi].push(item);
    }
  }

  const counts = buckets.map((b) =>
    Math.min(A3_MAX_FUELS_PER_PROCESS, Math.max(1, b.length))
  );
  const flatItems: Record<string, unknown>[] = [];
  for (let p = 0; p < P; p++) {
    const b = buckets[p];
    const c = counts[p];
    for (let j = 0; j < c; j++) {
      flatItems.push((b[j] as Record<string, unknown>) ?? {});
    }
  }
  return { counts, flatItems };
}

/** Exported function — shift A3Fuel Values For Insert. */
export function shiftA3FuelValuesForInsert(
  prev: Record<string, string>,
  insertAt: number,
  newRowCount: number,
  fieldNames: string[],
  code: string = FUEL_CODE
): Record<string, string> {
  return shiftA2FuelValuesForInsert(prev, insertAt, newRowCount, fieldNames, code);
}

/** Exported function — shift A3Fuel Values For Remove. */
export function shiftA3FuelValuesForRemove(
  prev: Record<string, string>,
  removeAt: number,
  oldRowCount: number,
  fieldNames: string[],
  code: string = FUEL_CODE
): Record<string, string> {
  return shiftA2FuelValuesForRemove(prev, removeAt, oldRowCount, fieldNames, code);
}
